import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { CustomerDTO } from '@six-pockets/shared';
import { cx } from '@/utils/cx';
import { formatPhone } from '@/utils/format';
import styles from '@/styles/components/CustomerSuggestList/style.scss';

interface CustomerSuggestListProps {
  /** The name field the list hangs under. */
  anchorRef: RefObject<HTMLElement | null>;
  id: string;
  results: CustomerDTO[];
  /** Index highlighted with the arrow keys; -1 for none. */
  active: number;
  onPick: (customer: CustomerDTO) => void;
  onClose: () => void;
}

/** Room kept between the list and the edge of the screen. */
const EDGE = 8;
const MIN_WIDTH = 260;
/** Below this much room under the field, the list opens above it. */
const ROOM_BELOW = 180;

/**
 * The existing customers offered under a name field (see useCustomerSuggest). Drawn on the page body at the field's
 * place on screen, since the fields it serves sit inside scrolling boxes (a table card's booking list, a modal body)
 * that would cut it off. It closes when anything behind it scrolls or the window is resized.
 */
export function CustomerSuggestList({ anchorRef, id, results, active, onPick, onClose }: CustomerSuggestListProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const width = Math.min(Math.max(r.width, MIN_WIDTH), window.innerWidth - 2 * EDGE);
    const left = Math.max(EDGE, Math.min(r.left, window.innerWidth - width - EDGE));
    const above = window.innerHeight - r.bottom < ROOM_BELOW && r.top > window.innerHeight - r.bottom;
    setPlace(above ? { left, width, bottom: window.innerHeight - r.top + 4 } : { left, width, top: r.bottom + 4 });
  }, [anchorRef, results.length]);

  useEffect(() => {
    const close = (e: Event) => {
      if (e.type === 'scroll' && listRef.current?.contains(e.target as Node)) return;
      onCloseRef.current();
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, []);

  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  return createPortal(
    <ul ref={listRef} id={id} role="listbox" aria-label="Existing customers" className={styles.list} style={place ?? { visibility: 'hidden' }}>
      {results.map((c, i) => (
        <li key={c.id} role="option" aria-selected={i === active}>
          {/* Pressing must not take the focus from the field: its blur would save the typed text first. */}
          <button type="button" tabIndex={-1} className={cx(styles.option, i === active && styles.active)} onPointerDown={(e) => e.preventDefault()} onClick={() => onPick(c)}>
            <span className={styles.name}>{c.name}</span>
            <span className={styles.phone}>{formatPhone(c.phone)}</span>
          </button>
        </li>
      ))}
    </ul>,
    document.body,
  );
}
